import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Confetti, MapPin, Clock, Ticket, WhatsappLogo, Copy, Share, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function PublicEvent() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [myRsvp, setMyRsvp] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${id}`);
      setEv(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Event not found"); }
    if (user && user !== false) {
      try {
        const { data } = await api.get("/events/rsvps/mine");
        setMyRsvp((data || []).find((r) => r.event_id === id) || null);
      } catch (err) { console.error("load rsvps failed", err); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user]);

  const shareUrl = `${window.location.origin}/e/${id}`;
  const shareText = ev ? `Join us at ${ev.title} — ${new Date(ev.event_date).toLocaleString()} @ ${ev.venue}. ${shareUrl}` : shareUrl;

  const shareWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(wa, "_blank");
  };
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: ev?.title, text: shareText, url: shareUrl }); } catch {}
    } else {
      shareWhatsApp();
    }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); } catch { toast.error("Couldn't copy"); }
  };

  const rsvp = async () => {
    if (!user || user === false) { nav(`/login?next=/e/${id}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/events/rsvp", { event_id: id });
      setMyRsvp(data); toast.success(`RSVP confirmed · ${data.ticket_number}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (err) return <div className="max-w-lg mx-auto px-6 py-16 samaaj-card p-8 text-center text-[#5C564D]">{err}</div>;
  if (!ev) return <div className="max-w-lg mx-auto px-6 py-16 text-center text-[#5C564D]">Loading…</div>;

  const isOrganizer = user && user !== false && user.id === ev.organizer_id;
  const isFull = ev.capacity && (myRsvp ? false : false); // we don't have live count here, backend enforces

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between">
        <Link to="/events" className="flex items-center gap-2 text-sm text-[#2C4A3B] hover:underline">← All events</Link>
        <Logo size={28} />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-8 items-start">
        <div className="rounded-3xl overflow-hidden bg-[#F2EBE5] border border-[#E5D9CC]" data-testid="event-poster">
          {ev.poster_url ? (
            <img src={ev.poster_url} alt={ev.title} className="w-full h-full object-cover" />
          ) : (
            <div className="aspect-[4/5] w-full flex items-center justify-center"><Confetti size={96} weight="duotone" className="text-[#2C4A3B]" /></div>
          )}
        </div>

        <div>
          <span className="samaaj-chip text-[10px] uppercase">{ev.category || "community"}</span>
          <h1 className="font-heading text-5xl mt-3 tracking-tight text-[#1C1B1A]" data-testid="event-title">{ev.title}</h1>
          <div className="mt-3 text-sm text-[#5C564D] flex items-center gap-2"><Clock size={14} /> {new Date(ev.event_date).toLocaleString()}</div>
          <div className="mt-1 text-sm text-[#5C564D] flex items-center gap-2"><MapPin size={14} /> {ev.venue}</div>
          <div className="mt-1 text-sm text-[#5C564D]">Hosted by {ev.organizer_name || "the community"}</div>

          <p className="mt-5 text-[#1C1B1A] whitespace-pre-wrap leading-relaxed">{ev.description}</p>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="font-heading text-3xl text-[#D95A41]">{ev.ticket_price ? `$${ev.ticket_price}` : "Free"}</div>
            {myRsvp ? (
              <div className="samaaj-chip text-xs flex items-center gap-1"><Ticket size={12} /> RSVP confirmed · {myRsvp.ticket_number}</div>
            ) : (
              <button data-testid="btn-rsvp-public" disabled={busy} onClick={rsvp} className="samaaj-btn-primary text-sm">{busy ? "Booking…" : "RSVP now"}</button>
            )}
            {myRsvp && (
              <Link to={`/events/ticket/${myRsvp.id}/print`} target="_blank" className="samaaj-btn-outline text-sm">Print my pass</Link>
            )}
          </div>
          {ev.ticket_price ? (
            <div className="mt-2 text-xs text-[#2C4A3B] font-semibold">Pay ${ev.ticket_price} at the gate — bring your printed pass.</div>
          ) : null}

          <div className="mt-8 border-t border-[#E5D9CC] pt-5">
            <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B] mb-2">Share</div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="btn-share-whatsapp" onClick={shareWhatsApp} className="samaaj-btn-outline text-xs flex items-center gap-1"><WhatsappLogo size={14} weight="fill" className="text-emerald-600" /> WhatsApp</button>
              <button data-testid="btn-share-native" onClick={shareNative} className="samaaj-btn-outline text-xs flex items-center gap-1"><Share size={14} /> Share…</button>
              <button data-testid="btn-copy-link" onClick={copyLink} className="samaaj-btn-outline text-xs flex items-center gap-1"><Copy size={14} /> Copy link</button>
            </div>
            <div className="mt-2 text-[10px] text-[#5C564D] break-all">{shareUrl}</div>
          </div>

          {isOrganizer && (
            <div className="mt-6">
              <Link to={`/events/${id}/roster`} data-testid="btn-view-roster" className="samaaj-btn-primary text-sm inline-flex items-center gap-1"><Users size={14} /> View attendee roster</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
